/** Content → Translations. Conventions: see `common.ts`. */
const translations: Record<string, string> = {
  title: 'Translations',
  description: 'Translate your products, pages and storefront text into every language you publish.',
  no_extra_languages: 'Your store has only one language. Enable more in Settings → Languages to start translating.',
  enable_languages: 'Enable languages',

  'summary.title': 'Progress',
  'summary.completion': '{percent}% complete',
  'summary.counts': '{complete} complete · {partial} partial · {missing} missing',

  'entity_types.product': 'Products',
  'entity_types.category': 'Categories',
  'entity_types.page': 'Pages',
  'entity_types.blog': 'Blogs',
  'entity_types.blog_post': 'Blog posts',
  'entity_types.menu': 'Menus',
  'entity_types.theme': 'Theme text',
  'entity_types.store': 'Store details',
  'entity_types.email_template': 'Email templates',
  'entity_types.metafield': 'Metafields',
  'entity_types.metaobject_entry': 'Metaobject entries',

  'entity_type.product': 'Product',
  'entity_type.category': 'Category',
  'entity_type.page': 'Page',
  'entity_type.blog': 'Blog',
  'entity_type.blog_post': 'Blog post',
  'entity_type.menu': 'Menu',
  'entity_type.theme': 'Theme',
  'entity_type.store': 'Store',
  'entity_type.email_template': 'Email template',
  'entity_type.metafield': 'Metafield',
  'entity_type.metaobject_entry': 'Metaobject entry',

  'status.complete': 'Complete',
  'status.partial': 'Partial',
  'status.missing': 'Missing',

  'source.ORIGINAL': 'Original',
  'source.MANUAL': 'Manual',
  'source.AI': 'AI',
  'source.IMPORT': 'Imported',
  'source.none': 'Not translated',

  'filters.entity_type': 'Content type',
  'filters.language': 'Language',
  'filters.status': 'Status',
  'filters.source': 'Source',
  'filters.search': 'Search by name',
  'filters.all_types': 'All content',
  'filters.all_languages': 'All languages',
  'filters.all_statuses': 'Any status',
  'filters.all_sources': 'Any source',
  'filters.clear': 'Clear filters',

  'table.item': 'Item',
  'table.type': 'Type',
  'table.updated': 'Updated',
  'table.translate': 'Translate',
  empty: 'There is nothing to translate yet.',
  empty_filtered: 'No items match these filters.',

  'editor.title': 'Translate {label}',
  'editor.back_to': 'Back to {label}',
  'editor.language': 'Language',
  'editor.source': 'Original ({language})',
  'editor.translation': 'Translation ({language})',
  'editor.required': 'Required',
  'editor.characters': '{count} / {max}',
  'editor.copy_source': 'Copy original',
  'editor.save': 'Save translation',
  'editor.saving': 'Saving…',
  'editor.saved': 'Translation saved.',
  'editor.delete': 'Delete translation',
  'editor.delete_confirm_title': 'Delete the {language} translation?',
  'editor.delete_confirm_body':
    'Shoppers will see the default language for this item until it is translated again.',
  'editor.deleted': 'Translation deleted.',
  'editor.unsaved': 'You have unsaved changes.',
  'editor.default_language_note': 'This is the default language. Editing it changes the original content.',
  'editor.fallback_note': 'Empty fields show the default language on the storefront.',
  'editor.no_fields': 'This item has no text to translate.',
  'editor.last_updated': 'Last updated {date}',
  'editor.back': 'Back to translations',

  'ai.translate': 'Translate with AI',
  'ai.translating': 'Translating…',
  'ai.preview_title': 'AI translation',
  'ai.preview_hint': 'Review the suggestion and edit anything before you save.',
  'ai.use': 'Use this translation',
  'ai.discard': 'Discard',
  'ai.review_notice': 'AI translations can contain mistakes. Please review before publishing.',
  'ai.remaining_one': '{count} AI generation left this month',
  'ai.remaining_other': '{count} AI generations left this month',
  'ai.unavailable': 'AI translation is not included in your plan.',

  'bulk.title': 'Translate in bulk with AI',
  'bulk.description': 'Fill in missing translations for many items at once.',
  'bulk.open': 'Bulk translate',
  'bulk.entity_type': 'Content type',
  'bulk.target_languages': 'Translate into',
  'bulk.scope_all': 'All items',
  'bulk.scope_selected_one': '{count} selected item',
  'bulk.scope_selected_other': '{count} selected items',
  'bulk.overwrite': 'What to translate',
  'bulk.overwrite.EMPTY_ONLY': 'Only empty fields',
  'bulk.overwrite.AI_ONLY': 'Empty fields and earlier AI translations',
  'bulk.overwrite_note': 'Manual and imported translations are never replaced.',
  'bulk.start': 'Start translating',
  'bulk.started': 'Translation started. You can leave this page while it runs.',
  'bulk.progress': '{completed} of {total}',
  'bulk.progress_detail': '{completed} of {total} done · {failed} failed · {skipped} skipped',
  'bulk.cancel': 'Cancel job',
  'bulk.cancel_confirm': 'Stop this job? Items already translated are kept.',
  'bulk.jobs_title': 'Recent jobs',
  'bulk.banner_running': 'Translating {type} into {languages}…',
  'bulk.banner_completed': 'Finished translating {type}.',
  'bulk.banner_failed': 'The translation of {type} stopped before it finished.',
  'bulk.banner_cancelled': 'The translation of {type} was cancelled.',
  'bulk.view_job': 'View job',
  'bulk.dismiss': 'Dismiss',
  'bulk.no_jobs': 'No translation jobs yet.',
  'bulk.errors_title': 'Errors',

  'job_status.PENDING': 'Queued',
  'job_status.RUNNING': 'Running',
  'job_status.COMPLETED': 'Completed',
  'job_status.FAILED': 'Failed',
  'job_status.CANCELLED': 'Cancelled',

  'export.title': 'Export',
  'export.button': 'Export CSV',
  'export.languages': 'Languages to export',
  'export.hint': 'Download a CSV to translate in a spreadsheet. Excel opens it with every script intact.',
  'export.exporting': 'Preparing file…',

  'import.title': 'Import',
  'import.button': 'Import CSV',
  'import.choose_file': 'Choose a CSV file',
  'import.hint': 'Upload a CSV exported from this page. Rows with an empty translation are skipped.',
  'import.dry_run': 'Check the file first',
  'import.dry_run_hint': 'Validate every row without saving anything.',
  'import.apply': 'Import translations',
  'import.importing': 'Importing…',
  'import.dry_run_result': '{rows} rows checked: {applied} ready to import, {skipped} skipped.',
  'import.result': '{applied} of {rows} rows imported, {skipped} skipped.',
  'import.errors_title': 'Rows with problems',
  'import.row_error': 'Row {row}: {message}',

  'preview.title': 'Preview',
  'preview.open': 'Preview on storefront',
  'preview.showing': 'Showing {language}',

  'errors.UNKNOWN_TRANSLATION_FIELD': 'That field cannot be translated.',
  'errors.LANGUAGE_NOT_ENABLED': 'That language is not enabled for your store.',
  'errors.CANNOT_DELETE_DEFAULT_TRANSLATION': 'The default language translation cannot be deleted.',
  'errors.TRANSLATION_ENTITY_NOT_FOUND': 'This item no longer exists.',
  'errors.RATE_LIMITED': 'Too many AI requests. Please wait a moment and try again.',
  'errors.PLAN_LIMIT_REACHED': 'Your plan does not include this. Upgrade to continue.',
  'errors.TRANSLATION_CONFLICT': 'This translation was saved elsewhere at the same time. Reload to see the latest version.',
  'errors.TRANSLATION_TOO_LONG': 'A translation is longer than that field allows.',
  'errors.TRANSLATION_INVALID_DOCUMENT': 'This translation no longer matches the content. Reload and try again.',
  'errors.UNKNOWN_TEMPLATE_VARIABLE': 'The email uses a placeholder this template does not have.',
  'errors.MISSING_REQUIRED_TEMPLATE_VARIABLE': 'The email is missing a placeholder it needs. Copy it from the original.',
  'errors.TRANSLATION_TARGET_IS_DEFAULT': 'The default language is your original content. Choose another language.',
  'errors.AI_RESPONSE_INVALID': 'The AI returned something unusable. Please try again.',
  'errors.AI_PROVIDER_TIMEOUT': 'The AI took too long to answer. Try again, or translate one section at a time.',
  'errors.AI_PROVIDER_UNAVAILABLE': 'The AI service is not reachable right now. Please try again shortly.',
  'errors.STREAM_INTERRUPTED': 'The connection dropped before the AI finished. Please try again.',
  'errors.INTERNAL_ERROR': 'Something went wrong while the AI was writing. Please try again.',
  'errors.FEATURE_DISABLED': 'AI translation is switched off for now. Please try again later.',
  'errors.UNKNOWN_TRANSLATION_ENTITIES': 'Some of the selected items no longer exist. Refresh and try again.',
  'errors.TRANSLATION_JOB_TOO_LARGE': 'That is too much to translate at once. Select fewer items or languages.',
  'errors.TRANSLATION_JOB_IN_PROGRESS': 'A translation of this type is already running. Wait for it to finish.',
  'errors.TRANSLATION_JOB_NOT_FOUND': 'That translation job no longer exists.',
  'errors.FILE_TOO_LARGE': 'The file is larger than 5 MB.',
  'errors.NOT_TEXT': 'The file is not UTF-8 text. Save it as CSV (UTF-8) and try again.',
  'errors.BAD_HEADER': 'The file does not have the export’s column headings.',
  'errors.EMPTY_FILE': 'The file is empty.',
  'errors.TOO_MANY_ROWS': 'The file has too many rows. Split it and import each part.',
  'errors.BAD_CSV': 'The file could not be read as CSV.',
  'errors.load_failed': 'We could not load translations.',
  'errors.save_failed': 'We could not save the translation.',
  'errors.ai_failed': 'AI translation failed. Please try again.',
  'errors.import_failed': 'We could not import that file.',
  'errors.export_failed': 'We could not export translations.',

  'page.single_language_title': 'Only one language is enabled',
  'page.select_page': 'Select all on this page',
  'page.select_row': 'Select {label}',
  'page.edit_row': 'Edit the translation of {label}',

  'ai.no_permission': 'Your role does not allow using AI. Ask the store owner for access.',
  'ai.apply_to_form': 'Apply to form',
  'ai.nothing': 'The AI returned no suggestions for these fields.',
  'ai.use_field': 'Use for {field}',
  'ai.progress_one': '{done} of {total} field translated',
  'ai.progress_other': '{done} of {total} fields translated',
  'ai.stop': 'Stop',
  'ai.stopped': 'Stopped. The fields that were finished can still be applied.',
  'ai.waiting': 'Waiting…',
  'ai.select_all': 'Select all',
  'ai.selected_count': '{selected} of {total} selected',
  'ai.manual_warning': 'This field already has a manual translation. Tick it only if you want to replace it.',
  'ai.flagged_warning': 'Check this translation: a placeholder or formatting tag may have been lost.',
  'ai.current': 'Current',
  'ai.proposed': 'Suggested',
  'ai.translate_field': 'Translate {field} with AI',

  'bulk.pending_one': '{pending} waiting',
  'bulk.pending_other': '{pending} waiting',
  'bulk.errors_count_one': '{count} error',
  'bulk.errors_count_other': '{count} errors',
  'bulk.fields': 'Fields',
  'bulk.all_fields': 'All fields',
  'bulk.overwrite_hint.EMPTY_ONLY': 'Fills in fields that have no translation yet. Nothing already translated changes.',
  'bulk.overwrite_hint.AI_ONLY':
    'Also refreshes fields that an earlier AI translation filled in. Manual translations are never replaced.',

  'import.checking': 'Checking…',
  'import.count.rows': 'Rows',
  'import.count.applied': 'Applied',
  'import.count.skipped': 'Skipped',
  'import.row': 'Row',
  'import.problem': 'Problem',

  'editor.breadcrumb': 'Breadcrumb',
  'editor.read_only': 'You have read-only access, so you can view translations but not change them.',
  'editor.general': 'General',
  'editor.keep_editing': 'Keep editing',
  'editor.switch_discard': 'Switch to {language}? Your unsaved changes in this language will be lost.',
  'editor.switch_discard_plain': 'You have unsaved translation changes. Switch language and lose them?',

  'theme.storefront_text': 'Storefront text',
  'theme.section_group': '{page} page · {section}',
  'theme.title': 'Translating into {language}',
  'theme.hint': 'Empty fields show the default language. Translations are saved separately from the theme.',
  'theme.translate_all': 'Translate all with AI',
  'theme.translate_group': 'Translate {group} with AI',
  'theme.original': 'Original:',
  'theme.editing_language': 'Editing language',
  'theme.default_option': '{language} (original)',
  'theme.translate': 'Translate',
  'theme.translate_named': 'Translate {name}',
  'theme.back_to_editing': 'Back to editing',
  'theme.show_all': 'Show all text in this theme',
  'theme.section_empty': 'This section has no text to translate yet.',
  'theme.unsaved_theme':
    'This theme has changes that are not saved. Save the theme first - text that is only in the draft cannot be translated yet.',
  'theme.save_theme': 'Save theme',
};

export default translations;
